import { useState } from 'react';
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type {
  OpportunityListFilters,
  OpportunityStatus,
  Decision,
} from '../../types/internal-opportunity.types';
import { STATUS_CONFIG, DECISION_CONFIG } from '../../types/internal-opportunity.types';

interface OpportunitiesFiltersProps {
  filters: OpportunityListFilters;
  onFiltersChange: (filters: OpportunityListFilters) => void;
  hideStatusFilters?: boolean;
}

const STATUSES: OpportunityStatus[] = [
  'new',
  'qualified',
  'in_progress',
  'submitted',
  'won',
  'lost',
  'abandoned',
];

const DECISIONS: Decision[] = ['go', 'no_go', 'pending'];

const MARKET_TYPES = [
  { value: 'services', label: 'Services' },
  { value: 'supplies', label: 'Fournitures' },
  { value: 'works', label: 'Travaux' },
];

const IMPORT_SOURCES = [
  { value: 'boamp', label: 'BOAMP' },
  { value: 'ted', label: 'TED' },
  { value: 'manual', label: 'Manuel' },
];

export function OpportunitiesFilters({
  filters,
  onFiltersChange,
  hideStatusFilters = false,
}: OpportunitiesFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || '');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltersChange({ ...filters, search: searchInput || undefined });
  };

  const handleStatusToggle = (status: OpportunityStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];
    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
    });
  };

  const handleReset = () => {
    setSearchInput('');
    onFiltersChange({});
  };

  const activeFiltersCount = [
    filters.search,
    filters.status?.length,
    filters.decision,
    filters.marketType,
    filters.importSource,
    filters.assignedToMe,
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Barre de recherche principale */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par titre, référence..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button type="submit" variant="default" className="bg-copper hover:bg-copper/90">
          Rechercher
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          Filtres
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {activeFiltersCount}
            </Badge>
          )}
          {showAdvanced ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </form>

      {/* Filtres rapides par statut */}
      {!hideStatusFilters && (
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((status) => {
            const config = STATUS_CONFIG[status];
            const isActive = filters.status?.includes(status);
            return (
              <Button
                key={status}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleStatusToggle(status)}
                className={isActive ? `${config.bgColor} ${config.color} border-0` : ''}
              >
                {config.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Filtres avancés */}
      {showAdvanced && (
        <div className="p-4 border rounded-lg bg-muted/30 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Décision */}
            <div className="space-y-2">
              <Label>Décision</Label>
              <Select
                value={filters.decision || ''}
                onValueChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    decision: value ? (value as Decision) : undefined,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Toutes</SelectItem>
                  {DECISIONS.map((decision) => (
                    <SelectItem key={decision} value={decision}>
                      {DECISION_CONFIG[decision].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type de marché */}
            <div className="space-y-2">
              <Label>Type de marché</Label>
              <Select
                value={filters.marketType || ''}
                onValueChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    marketType: value || undefined,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tous</SelectItem>
                  {MARKET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div className="space-y-2">
              <Label>Source</Label>
              <Select
                value={filters.importSource || ''}
                onValueChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    importSource: value || undefined,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Toutes</SelectItem>
                  {IMPORT_SOURCES.map((source) => (
                    <SelectItem key={source.value} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date limite */}
            <div className="space-y-2">
              <Label>Date limite après</Label>
              <Input
                type="date"
                value={filters.deadlineFrom || ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    deadlineFrom: e.target.value || undefined,
                  })
                }
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-4 pt-2 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="assignedToMe"
                checked={filters.assignedToMe || false}
                onCheckedChange={(checked) =>
                  onFiltersChange({
                    ...filters,
                    assignedToMe: checked === true ? true : undefined,
                  })
                }
              />
              <Label htmlFor="assignedToMe" className="cursor-pointer">
                Mes opportunités uniquement
              </Label>
            </div>

            <div className="ml-auto">
              <Button variant="ghost" size="sm" onClick={handleReset}>
                <X className="h-4 w-4 mr-1" />
                Réinitialiser les filtres
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
